import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { CanActivateChild, ActivatedRouteSnapshot, UrlTree, Router } from '@angular/router';
import { isPlatformServer } from '@angular/common';

import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { MaintenanceConfigService, MaintenanceConfigFeature, AppState } from '@fcom/core';

import { getStage } from '../store/selectors';
import { Stage, UpgradeError } from '../interfaces';
import { setError, setStage } from '../store/actions/status.actions';
import { getParentPath } from '../utils';

@Injectable()
export class OneUpgradeGuard implements CanActivateChild {
  constructor(
    private maintenanceConfigService: MaintenanceConfigService,
    private store$: Store<AppState>,
    private router: Router,
    @Inject(PLATFORM_ID) private platform: string
  ) {}

  mapPathToStage(route: ActivatedRouteSnapshot): Stage {
    const path = route?.url?.[0]?.path;
    const matchedStage = Object.keys(Stage).find((key) => Stage[key] === path);

    return Stage[matchedStage] || Stage.LOGIN;
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    if (this.mapPathToStage(childRoute) !== Stage.LOGIN && isPlatformServer(this.platform)) {
      return of(true);
    }

    const parentPath = getParentPath(childRoute);

    return combineLatest([
      this.maintenanceConfigService.getStatusForFeature(MaintenanceConfigFeature.ONE_UPGRADE),
      this.store$.pipe(select(getStage)),
    ]).pipe(
      take(1),
      map(([isDisabled, stage]) => {
        const pathAsStage = this.mapPathToStage(childRoute);

        // Block access when feature is disabled due to maintenance break
        if (isDisabled && pathAsStage !== Stage.ERROR) {
          this.store$.dispatch(setError({ error: UpgradeError.maintenance }));
          return this.router.parseUrl(`${parentPath}/${Stage.ERROR}`);
        }

        // Browser back button changing the url but not the stage in the store.
        if (stage !== pathAsStage) {
          this.store$.dispatch(setStage({ stage: pathAsStage || Stage.LOGIN }));
        }

        return true;
      })
    );
  }
}
